import React, { useState } from 'react';
import { Container, Box, Typography, Grid, Card, CardContent, Stack, TextField, Button, Link, Alert } from '@mui/material';
import emailjs from '@emailjs/browser';

const Contact: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // EmailJS configuration via environment variables
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID as string;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CONTACT as string;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY as string;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.error('EmailJS env vars missing for contact form', { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY });
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    // Prepare email template parameters
    const templateParams = {
      from_name: name || 'Website visitor',
      from_email: email,
      to_name: 'SuperGauge Team',
      message: `
Contact form submission from SuperGauge website:

Name: ${name}
Email: ${email}

Message:
${message}

---
This message was submitted from the SuperGauge contact page.
      `.trim(),
    };

    try {
      // Initialize EmailJS (only needed once)
      if (!(window as any).__emailjs_inited) {
        emailjs.init(PUBLIC_KEY);
        (window as any).__emailjs_inited = true;
      }

      // Send email
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams
      );

      // Set form as submitted on success
      setSubmitStatus('success');

      // Clear form
      setName('');
      setEmail('');
      setMessage('');

      console.log('Contact email sent successfully');
    } catch (error) {
      console.error('Failed to send contact email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero (mimics Industries hero) */}
      <Box
        className="platform-hero industries-hero"
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 12 },
          background:
            'radial-gradient(1200px 600px at 50% 15%, rgba(0,67,254,0.18) 0%, rgba(3,163,255,0.08) 45%, rgba(255,255,255,1) 75%)',
        }}
      >
        <Container className="slider-container" maxWidth="lg">
          <Box className="platform-hero__content" sx={{ textAlign: 'center', maxWidth: 920, mx: 'auto' }}>
            <Typography
              className="platform-hero__title"
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                fontSize: { xs: 36, sm: 44, md: 56 },
                lineHeight: 1.1,
              }}
            >
              Let’s talk about your team
            </Typography>
            <Typography
              className="platform-hero__subtitle"
              variant="h6"
              color="text.secondary"
              sx={{ mt: 2, maxWidth: 820, mx: 'auto' }}
            >
              We’d love to hear from you—whether you have a question about the product, want to see a demo, or just
              want to explore if SuperGauge is a good fit for your organisation.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Send us a message</Typography>
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  {submitStatus === 'success' && (
                    <Alert severity="success">
                      Thank you for your message! We'll get back to you soon.
                    </Alert>
                  )}
                  {submitStatus === 'error' && (
                    <Alert severity="error">
                      Sorry, there was an error sending your message. Please try again or contact us directly.
                    </Alert>
                  )}
                  <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    disabled={isSubmitting}
                  />
                  <TextField
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    minRows={4}
                    required
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ alignSelf: 'flex-start' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send'}
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Site footer (shared with landing/platform pages) */}
      <Box className="site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        {/* Middle nav columns */}
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography
                className="site-footer__branddesc"
                variant="body2"
                sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}
              >
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* Reserved for future footer nav links */}
            </Grid>
          </Grid>
        </Container>

        {/* Bottom bar */}
        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box
            className="site-footer__bar-inner"
            sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}
          >
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">
                  © {new Date().getFullYear()} SuperGauge
                </Typography>
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">
                    Contact
                  </Typography>
                  <Typography className="site-footer__link" variant="caption">
                    Terms
                  </Typography>
                  <Typography className="site-footer__link" variant="caption">
                    Privacy policy
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Contact;
